/**
 * Routes where the global chat panel is hidden entirely.
 *
 *   - `/home` (and `/`, the landing rewrite) render their own full-page
 *     `AppChatLauncher` hero composer — the side panel would duplicate
 *     it and fight the layout.
 *
 * `/chat/<id>` and `/chats` are NOT excluded — they are the FULL-WIDTH chat
 * surfaces (the thread and the chat-list overview respectively; see
 * `isFullChatPath` / `parseFullChatId` / `isFullChatListPath`), handled as
 * their own shell mode.
 *
 * On excluded paths the shell renders the header + content without the
 * resizable chat panel, and the header chat toggle is hidden.
 */
const EXCLUDED_PREFIXES: ReadonlyArray<string> = ['/home']

/** Exact-match exclusions (no subpath semantics). */
const EXCLUDED_EXACT: ReadonlyArray<string> = ['/']

export const EXCLUDED_CHAT_PATHS: ReadonlyArray<string> = [
  ...EXCLUDED_EXACT,
  ...EXCLUDED_PREFIXES,
]

/** True when the given pathname should NOT show the global chat panel. */
export function isExcludedChatPath(
  pathname: string | null | undefined
): boolean {
  if (!pathname) {
    return false
  }
  if (EXCLUDED_EXACT.includes(pathname)) {
    return true
  }
  return EXCLUDED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

/**
 * Parses the chatId out of a full-page chat path (`/chat/<id>`). Returns
 * `null` for `/chat` (the index redirect) and any non-chat route. The
 * full-page chat is the chat at 100% width; its URL is the canonical
 * representation of "chat is expanded to full screen".
 */
export function parseFullChatId(
  pathname: string | null | undefined
): string | null {
  if (!pathname) {
    return null
  }
  const match = /^\/chat\/([^/?#]+)/.exec(pathname)
  return match ? decodeURIComponent(match[1]!) : null
}

/**
 * Canonical href for a full-width thread. Inverse of {@link parseFullChatId};
 * every `/chat/<id>` push or `<Link>` should go through here so encoding
 * stays one place (ids are unguessable, but they are not always URL-safe).
 */
export function fullChatPath(chatId: string): string {
  return `/chat/${encodeURIComponent(chatId)}`
}

/** True when the pathname is a full-page chat route (`/chat/<id>`). */
export function isFullChatPath(pathname: string | null | undefined): boolean {
  return parseFullChatId(pathname) !== null
}

/**
 * The full-width chat-LIST surface. The chat panel renders at 100% width
 * showing the recent-chats overview instead of a thread — the list twin of
 * `/chat/<id>`, reached from the sidebar "Chats" tab when no side panel is
 * open (collapsed panel, or an excluded route such as `/home`).
 */
export const FULL_CHAT_LIST_PATH = '/chats'

/** True when the pathname is the full-width chat-list overview (`/chats`). */
export function isFullChatListPath(
  pathname: string | null | undefined
): boolean {
  if (!pathname) {
    return false
  }
  // Ignore any query / hash the caller left on the path.
  const bare = pathname.split(/[?#]/, 1)[0]
  return bare === FULL_CHAT_LIST_PATH || bare === `${FULL_CHAT_LIST_PATH}/`
}

/**
 * The three layout modes the global chat shell renders, derived purely
 * from the pathname:
 *
 *   - `full`     — `/chat/<id>` and `/chats`: chat at 100% width. The
 *                  thread route hides the app header; `/chats` keeps it
 *                  (title "Chats") above the list. Which VIEW the panel
 *                  shows is decided by `resolveChatPanelView`
 *                  (`./chat-panel-view-state.ts`).
 *   - `excluded` — `/home`, `/`: no chat (content only).
 *   - `split`    — everything else: chat panel beside the page content.
 *
 * `full` takes precedence over `excluded` so a future overlap can't hide
 * the full-page chat. Exported separately from the shell so the routing
 * contract is unit-testable.
 */
export type ChatShellMode = 'full' | 'excluded' | 'split'

export function resolveChatShellMode(
  pathname: string | null | undefined
): ChatShellMode {
  if (isFullChatPath(pathname) || isFullChatListPath(pathname)) {
    return 'full'
  }
  if (isExcludedChatPath(pathname)) {
    return 'excluded'
  }
  return 'split'
}
